/**
 * Timeline Ruler Component
 *
 * Displays time ruler with tick marks and labels showing seconds/frames.
 */

import { useEffect, useMemo, useRef } from 'react'
import { generateRulerTicks } from './timelineUtils'
import { createNadDebug } from '@ponpon/api-client'

// RFC-0631 round-3 (Issue 4): observability for the first-tick ("0s") anchor.
// The first tick label used to be centered (-translate-x-1/2), so half of it
// was clipped on the left. We now anchor it to the tick line and log the
// delta between the first tick position and the clip-row origin so NAD
// traces can confirm the alignment.
const layoutLog = createNadDebug('dw:video-editor:layout')

interface TimelineRulerProps {
  /** Total duration of timeline in seconds */
  totalDuration: number

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Viewport width in pixels */
  viewportWidth: number

  /** Height of ruler in pixels */
  height?: number
}

export function TimelineRuler({
  totalDuration,
  zoomLevel,
  viewportWidth,
  height = 30,
}: TimelineRulerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const loggedRef = useRef(false)
  const ticks = useMemo(
    () => generateRulerTicks(totalDuration, zoomLevel, viewportWidth),
    [totalDuration, zoomLevel, viewportWidth]
  )

  const timelineWidth = Math.max(totalDuration * zoomLevel, viewportWidth)

  // Log the first-tick origin once per mount. The right-column (Issue 3)
  // ruler shares its origin with the clip rows, so this `firstTickPx` /
  // `firstClipLeftPx` delta should be 0 after the fix.
  useEffect(() => {
    if (loggedRef.current) return
    if (ticks.length === 0) return
    loggedRef.current = true
    try {
      const el = containerRef.current
      const firstTickPx = ticks[0]?.position ?? 0
      const containerLeft = el ? el.getBoundingClientRect().left : 0
      layoutLog({
        event: 'ruler-origin-anchor',
        firstTickPx,
        containerLeft,
        delta: firstTickPx,
        zoomLevel,
      })
    } catch {
      // best-effort
    }
  }, [ticks, zoomLevel])

  return (
    <div
      ref={containerRef}
      className="relative bg-canvas border-b border-border"
      style={{ width: `${timelineWidth}px`, height: `${height}px` }}
    >
      {/* Tick marks */}
      {ticks.map((tick, index) => (
        <div
          key={index}
          className="absolute top-0"
          style={{ left: `${tick.position}px` }}
        >
          {/* Tick line */}
          <div
            className={`${tick.isMajor ? 'bg-white/40 h-4' : 'bg-border h-2'} w-px`}
          />

          {/* Tick label */}
          {tick.label && (
            <div
              className={`absolute top-4 left-0 text-text-muted text-xs whitespace-nowrap ${
                // RFC-0631 round-3 (Issue 4): the first label (0s) anchors
                // to the tick line (no centering) so it isn't clipped on
                // the left of the scroll area; later labels stay centered.
                index === 0 ? 'translate-x-0 text-left' : '-translate-x-1/2'
              }`}
            >
              {tick.label}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
